import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trash2 } from "lucide-react";

interface SetItem {
  id: string;
  number: number;
  reps: number;
  minutes: number;
  seconds: number;
  distance: number | "";
  energy_zone: string;
  description?: string;
}

interface SetRowProps {
  set: SetItem;
  energyZones: string[];
  onUpdate: (id: string, field: keyof SetItem, value: any) => void;
  onDelete: (set: SetItem) => void;
}


const normalizeTimeValue = (value: string, max: number) => {
  if (value === "" || value === null) return 0;

  const num = Number(value);
  if (Number.isNaN(num)) return 0;

  return Math.min(Math.max(num, 0), max);
};


/**
 * Component for editing a single set row in desktop view
 */
export function SetRow({ set, energyZones, onUpdate, onDelete }: SetRowProps) {
  return (
    <div className="grid grid-cols-12 gap-4 px-4 py-3 border border-[0.5px] rounded-lg hover:bg-accent/50 transition-colors">

      {/* Reps */}
      <div className="col-span-2 flex items-center space-x-2">
        <Input
          type="number"
          value={set.reps}
          onChange={(e) => {
            const value = parseInt(e.target.value) || 0;
            const maxReps = 1000;
            const validatedValue = Math.min(Math.max(0, value), maxReps);
            onUpdate(set.id, "reps", validatedValue);
          }}
          className="w-16 text-center"
          min="1"
          max="1000"
        />
        <span className="text-muted-foreground">x</span>
      </div>

      {/* Distance */}
      <div className="col-span-2 flex items-center space-x-2">
        <Input
            type="number"
            value={set.distance === "" ? "" : set.distance}
            onChange={(e) => {
              const raw = e.target.value;

              // Allow clearing input while typing
              if (raw === "") {
                onUpdate(set.id, "distance", "");
                return;
              }

              const value = Number(raw);
              if (Number.isNaN(value)) return;

              const maxDistance = 10000;
              const validatedValue = Math.min(Math.max(value, 0), maxDistance);

              onUpdate(set.id, "distance", validatedValue);
            }}
            onBlur={() => {
              // Restore default if left empty
              if (set.distance === "") {
                onUpdate(set.id, "distance", 25);
              }
            }}
            className="w-20 text-center"
            min={0}
            max={10000}
          />

        <span className="text-muted-foreground">m</span>
      </div>

      {/* Interval */}
      <div className="col-span-2 flex items-center space-x-2">
       <Input
          type="number"
          value={set.minutes}
          onChange={(e) =>
            onUpdate(
              set.id,
              "minutes",
              normalizeTimeValue(e.target.value, 59)
            )
          }
          className="w-14 text-center"
          min={0}
          max={59}
        />

        <span className="text-muted-foreground">:</span>
        <Input
          type="number"
          value={set.seconds}
          onChange={(e) =>
            onUpdate(
              set.id,
              "seconds",
              normalizeTimeValue(e.target.value, 59)
            )
          }
          className="w-14 text-center"
          min={0}
          max={59}
        />

      </div>

      {/* Energy Zone */}
      <div className="col-span-3">
        <Select
          value={set.energy_zone}
          onValueChange={(value) => onUpdate(set.id, "energy_zone", value)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select zone" />
          </SelectTrigger>
          <SelectContent>
            {energyZones.map((zone) => (
              <SelectItem key={zone} value={zone}>
                {zone}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Description inline */}
      <div className="col-span-2 flex items-center">
        <Input
          type="text"
          value={set.description || ""}
          placeholder="Description"
          onChange={(e) => onUpdate(set.id, "description", e.target.value)}
          className="text-sm"
        />
      </div>

      {/* Delete */}
      <div className="col-span-1 flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(set)}
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

    </div>
  );
}
